import React, { useState, useEffect, useMemo } from 'react';
import { 
  BookOpen, 
  Sparkles, 
  Calendar, 
  FileText, 
  Eye, 
  Edit3, 
  Save, 
  CheckCircle, 
  Search, 
  Filter, 
  ExternalLink, 
  CornerDownRight, 
  Check, 
  Cloud, 
  CloudLightning, 
  ArrowLeft, 
  Info,
  CalendarDays,
  PlusCircle,
  HelpCircle,
  Globe,
  Copy,
  Share2,
  X
} from 'lucide-react';
import { db, doc, setDoc, getDocs, collection, query, secureSetDoc } from '../firebase';
import { MemberRoomArticle, generateArticleSchedule } from '../data/memberroom_seed';

interface MemberRoomViewProps {
  onNavigateHome: () => void;
  currentUser?: any;
}

export const MemberRoomView: React.FC<MemberRoomViewProps> = ({ onNavigateHome, currentUser }) => {
  const [articles, setArticles] = useState<MemberRoomArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<MemberRoomArticle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDay, setFilterDay] = useState<string>('all');
  const [filterPublished, setFilterPublished] = useState<string>('all');
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCloudSynced, setIsCloudSynced] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [publishedModalArticle, setPublishedModalArticle] = useState<MemberRoomArticle | null>(null);

  // Load articles: try Firestore first, then fallback to LocalStorage, then fallback to pre-written Seed Data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'memberroom'));
        if (!querySnapshot.empty) {
          const loaded: MemberRoomArticle[] = [];
          querySnapshot.forEach((docSnap) => {
            loaded.push(docSnap.data() as MemberRoomArticle);
          });
          // Sort by day number and article number
          loaded.sort((a, b) => {
            if (a.dayNumber !== b.dayNumber) return a.dayNumber - b.dayNumber;
            return a.articleNumber - b.articleNumber;
          });
          setArticles(loaded);
          setIsCloudSynced(true);
        } else {
          // If Firestore is empty, load from LocalStorage or use the 120-article seed
          const localSaved = localStorage.getItem('giffarine_memberroom_articles');
          if (localSaved) {
            const parsed = JSON.parse(localSaved);
            setArticles(parsed);
          } else {
            const seed = generateArticleSchedule();
            setArticles(seed);
            // Save seed to local storage initially
            localStorage.setItem('giffarine_memberroom_articles', JSON.stringify(seed));
          }
        }
      } catch (err) {
        console.warn('Failed to fetch from Firestore, falling back to local database:', err);
        const localSaved = localStorage.getItem('giffarine_memberroom_articles');
        if (localSaved) {
          setArticles(JSON.parse(localSaved));
        } else {
          const seed = generateArticleSchedule();
          setArticles(seed);
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Set the first article (Day 1, Article 1) as selected by default
  useEffect(() => {
    if (articles.length > 0 && !selectedArticle) {
      const first = articles.find(a => a.id === 'day-1-art-1') || articles[0];
      setSelectedArticle(first);
    }
  }, [articles, selectedArticle]);

  // Sync / Save specific article to Cloud Database (Firestore) & LocalStorage
  const handleSaveArticle = async (articleToSave: MemberRoomArticle) => {
    setSaveStatus('saving');
    // Ensure published flag is marked true on publish
    const updated: MemberRoomArticle = {
      ...articleToSave,
      published: true
    };

    try {
      // 1. Save to local state
      const nextArticles = articles.map(a => a.id === updated.id ? updated : a);
      setArticles(nextArticles);
      setSelectedArticle(updated);
      localStorage.setItem('giffarine_memberroom_articles', JSON.stringify(nextArticles));

      // 2. Save to Firestore Cloud Database
      const docRef = doc(db, 'memberroom', updated.id);
      await setDoc(docRef, updated);

      setIsCloudSynced(true);
      setSaveStatus('saved');
      setSaveMessage('บันทึกข้อมูลบทความและเผยแพร่ลง Cloud สำเร็จ!');
      setPublishedModalArticle(updated);
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      console.error('Firebase save error:', err);
      setSaveStatus('error');
      setSaveMessage('ไม่สามารถบันทึกลง Cloud ได้ (เซฟลงเครื่องออฟไลน์ของคุณเรียบร้อยแล้ว)');
      setTimeout(() => setSaveStatus('idle'), 4000);
    }
  };

  const handleOpenLiveArticle = (slug: string) => {
    const clean = decodeURIComponent(slug).replace(/^\/|\/$/g, '');
    window.history.pushState({}, '', `/${clean}`);
    window.dispatchEvent(new Event('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopySlugUrl = (slug: string) => {
    const clean = decodeURIComponent(slug).replace(/^\/|\/$/g, '');
    const origin = window.location.origin;
    const full = `${origin}/${clean}`;
    navigator.clipboard.writeText(full);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2500);
  };

  // Sync entire 120-article structure to Cloud Database
  const handleSyncAllToCloud = async () => {
    setSaveStatus('saving');
    try {
      let count = 0;
      for (const art of articles) {
        const docRef = doc(db, 'memberroom', art.id);
        await setDoc(docRef, art);
        count++;
      }
      setIsCloudSynced(true);
      setSaveStatus('saved');
      setSaveMessage(`ซิงค์โครงสร้างบทความ 60 วันทั้งหมด (${count} บทความ) ลง Cloud Firestore สำเร็จ!`);
      setTimeout(() => setSaveStatus('idle'), 3500);
    } catch (err) {
      console.error('Failed to sync all:', err);
      setSaveStatus('error');
      setSaveMessage('เกิดข้อผิดพลาดในการซิงค์ฐานข้อมูลคลาวด์');
      setTimeout(() => setSaveStatus('idle'), 4000);
    }
  };

  // Filtered articles list based on search and drop-downs
  const filteredArticles = useMemo(() => {
    return articles.filter(art => {
      const matchesSearch = 
        art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        art.targetKeyword.toLowerCase().includes(searchQuery.toLowerCase()) ||
        art.content.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesDay = filterDay === 'all' ? true : art.dayNumber === parseInt(filterDay);
      const matchesPub = filterPublished === 'all' 
        ? true 
        : filterPublished === 'published' ? art.published : !art.published;

      return matchesSearch && matchesDay && matchesPub;
    });
  }, [articles, searchQuery, filterDay, filterPublished]);

  // Render a basic markdown parser with elegant Tailwind styling and registration links
  const renderMarkdown = (text: string) => {
    if (!text) return null;
    const lines = text.split('\n');
    let inTable = false;
    let tableHeaders: string[] = [];
    let tableRows: string[][] = [];

    const elements = lines.map((line, idx) => {
      const trimmed = line.trim();

      // Table Parsing
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        inTable = true;
        const parts = trimmed.split('|').map(p => p.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1);
        if (line.includes('---')) {
          // Separator, ignore
          return null;
        }
        if (tableHeaders.length === 0) {
          tableHeaders = parts;
        } else {
          tableRows.push(parts);
        }
        
        // Render table only at the end of the lines block or when table ends
        const nextLine = lines[idx + 1]?.trim();
        if (!nextLine || !nextLine.startsWith('|')) {
          const finishedHeaders = [...tableHeaders];
          const finishedRows = [...tableRows];
          tableHeaders = [];
          tableRows = [];
          inTable = false;

          return (
            <div key={`table-${idx}`} className="my-6 overflow-x-auto rounded-xl border border-pink-100 shadow-sm bg-white">
              <table className="w-full text-left border-collapse text-xs sm:text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-pink-50 to-rose-50 text-slate-700 font-bold border-b border-pink-100">
                    {finishedHeaders.map((h, i) => (
                      <th key={i} className="px-4 py-3 sm:px-6">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-pink-50 text-slate-600">
                  {finishedRows.map((row, rIdx) => (
                    <tr key={rIdx} className="hover:bg-pink-50/20 transition-colors">
                      {row.map((cell, cIdx) => (
                        <td key={cIdx} className="px-4 py-3 sm:px-6">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      }

      if (inTable) return null; // Wait for table processing

      // Headers
      if (trimmed.startsWith('# ')) {
        return <h1 key={idx} className="text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-800 mt-8 mb-4 border-b pb-2 tracking-tight">{trimmed.replace('# ', '')}</h1>;
      }
      if (trimmed.startsWith('## ')) {
        return <h2 key={idx} className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800 mt-6 mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#E60067] shrink-0" />{trimmed.replace('## ', '')}</h2>;
      }
      if (trimmed.startsWith('### ')) {
        return <h3 key={idx} className="text-base sm:text-lg font-bold text-slate-800 mt-4 mb-2">{trimmed.replace('### ', '')}</h3>;
      }

      // Bullet Points
      if (trimmed.startsWith('* ')) {
        return (
          <div key={idx} className="flex items-start gap-2.5 my-2.5 text-xs sm:text-sm text-slate-600 pl-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E60067] mt-2 shrink-0" />
            <span className="flex-1">{trimmed.replace('* ', '')}</span>
          </div>
        );
      }
      if (trimmed.startsWith('- ')) {
        return (
          <div key={idx} className="flex items-start gap-2.5 my-2.5 text-xs sm:text-sm text-slate-600 pl-4">
            <CornerDownRight className="w-4 h-4 text-pink-500 mt-0.5 shrink-0" />
            <span className="flex-1">{trimmed.replace('- ', '')}</span>
          </div>
        );
      }

      // Image Placement
      if (trimmed.startsWith('[รูปภาพประกอบ:')) {
        const altText = trimmed.match(/Alt:\s*([^\]]+)/)?.[1] || "สมัครกิฟฟารีนออนไลน์";
        const titleText = trimmed.match(/รูปภาพประกอบ:\s*([^\](]+)/)?.[1] || "รูปภาพประกอบ";
        return (
          <div key={idx} className="my-6 p-6 rounded-2xl bg-gradient-to-br from-pink-50 via-rose-50/50 to-white border border-pink-100 flex flex-col items-center justify-center text-center shadow-xs">
            <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 mb-2">
              <FileText className="w-6 h-6" />
            </div>
            <p className="text-xs sm:text-sm font-semibold text-slate-700">{titleText.trim()}</p>
            <span className="text-[10px] bg-pink-50 text-[#E60067] px-2.5 py-0.5 rounded-full border border-pink-100 font-mono mt-1">Alt Tags: {altText}</span>
          </div>
        );
      }

      // Styled Giffarine Registration Action Button
      if (trimmed.startsWith('🔘 **[สมัครสมาชิกกิฟฟารีนออนไลน์กับทีม PinkGiff]**')) {
        return (
          <div key={idx} className="my-6 flex flex-col items-center justify-center text-center p-1">
            <a 
              href="https://www.giffarine.com/member/register/132000061"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 sm:px-8 sm:py-4 bg-gradient-to-r from-[#E60067] to-pink-500 hover:from-pink-600 hover:to-rose-500 text-white font-extrabold text-sm sm:text-base rounded-2xl shadow-lg shadow-pink-500/20 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 text-center uppercase tracking-wide group"
            >
              <CheckCircle className="w-5 h-5 animate-pulse shrink-0" />
              <span>สมัครสมาชิกกิฟฟารีนออนไลน์กับทีม PinkGiff</span>
              <ExternalLink className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition-transform shrink-0" />
            </a>
          </div>
        );
      }

      // Sub-links/Action steps inside content
      if (trimmed.startsWith('👉 [') && trimmed.endsWith(')')) {
        const textMatch = trimmed.match(/\[([^\]]+)\]/);
        const textLabel = textMatch ? textMatch[1] : "สมัครสมาชิกออนไลน์";
        return (
          <div key={idx} className="my-3 text-center">
            <a 
              href="https://www.giffarine.com/member/register/132000061"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-[#E60067] hover:text-pink-700 font-bold underline decoration-pink-300 decoration-2 underline-offset-4 transition-all"
            >
              <span>{textLabel}</span>
              <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            </a>
          </div>
        );
      }

      // Blockquotes / Warnings
      if (trimmed.startsWith('> ')) {
        return (
          <div key={idx} className="my-5 p-4 rounded-xl border-l-4 border-[#E60067] bg-pink-50/40 text-xs sm:text-sm text-slate-700 leading-relaxed italic">
            {trimmed.replace('> ', '')}
          </div>
        );
      }

      // Empty Lines
      if (trimmed === '') return <div key={idx} className="h-3" />;

      // Normal Paragraphs
      return <p key={idx} className="text-xs sm:text-sm text-slate-600 leading-relaxed my-3.5">{line}</p>;
    });

    return <div className="space-y-1">{elements}</div>;
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['Prompt',sans-serif] text-slate-800">
      {/* Dynamic Floating Save Indicator */}
      {saveStatus !== 'idle' && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-5 py-4 rounded-2xl shadow-xl border text-xs sm:text-sm font-bold transition-all animate-bounce ${
          saveStatus === 'saving' ? 'bg-white border-pink-200 text-slate-700' :
          saveStatus === 'saved' ? 'bg-emerald-500 border-emerald-600 text-white' :
          'bg-rose-500 border-rose-600 text-white'
        }`}>
          {saveStatus === 'saving' && (
            <div className="w-5 h-5 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" />
          )}
          {saveStatus === 'saved' && <Check className="w-5 h-5" />}
          <span>{saveMessage}</span>
        </div>
      )}

      {/* Top Banner Header */}
      <header className="bg-gradient-to-r from-[#E60067] to-pink-500 text-white py-8 px-4 sm:px-6 shadow-md">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="space-y-1">
            <button 
              onClick={onNavigateHome}
              className="inline-flex items-center gap-1.5 text-xs bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-full text-white font-bold transition-colors mb-3 cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> กลับหน้าหลักร้านค้า
            </button>
            <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2.5">
              <BookOpen className="w-8 h-8" /> 
              <span>MemberRoom Content System</span>
              <span className="text-xs font-semibold bg-amber-400 text-slate-900 px-3 py-1 rounded-full border border-amber-300">
                ระบบจัดการรับสมัครออนไลน์ 60 วัน
              </span>
            </h1>
            <p className="text-xs sm:text-sm text-pink-100 font-medium">
              ตารางเรียนรู้และบทความคัดสรรเพื่อดึงดูดผู้สนใจสมัครสมาชิกกิฟฟารีนผ่านทีม PinkGiff อย่างเป็นทางการ
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-start md:self-center">
            {isCloudSynced ? (
              <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 text-xs font-bold px-3 py-1.5 rounded-full">
                <Cloud className="w-4 h-4" /> เชื่อมต่อคลาวด์เสร็จสมบูรณ์
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-amber-500/20 text-amber-200 border border-amber-400/40 text-xs font-bold px-3 py-1.5 rounded-full">
                <CloudLightning className="w-4 h-4 animate-pulse" /> มีข้อมูลที่ยังไม่ได้เซฟลง Cloud
              </span>
            )}
            <button
              onClick={handleSyncAllToCloud}
              className="bg-white text-[#E60067] hover:bg-pink-50 font-extrabold text-xs sm:text-sm px-4 py-2 rounded-xl shadow-md transition-all active:scale-95 cursor-pointer flex items-center gap-1"
            >
              <Cloud className="w-4 h-4" /> ซิงค์บทความทั้งหมดลงคลาวด์
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Side: 60-Day Article Calendar Scheduler Dashboard */}
        <section className="lg:col-span-5 space-y-4">
          <div className="bg-white rounded-3xl p-4 sm:p-5 shadow-xs border border-pink-100">
            <h2 className="text-base sm:text-lg font-bold text-slate-800 flex items-center gap-2 mb-4">
              <CalendarDays className="w-5 h-5 text-[#E60067]" />
              <span>ตารางเผยแพร่บทความ (วันละ 2 บทความ)</span>
            </h2>

            {/* Filters Bar */}
            <div className="space-y-3 mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input 
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ค้นหาตามหัวข้อหรือคีย์เวิร์ด..."
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm placeholder-slate-400 focus:outline-none focus:border-[#E60067] focus:ring-1 focus:ring-pink-500/25 transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-1.5 bg-slate-50 rounded-xl px-2 py-1.5 border border-slate-100">
                  <span className="text-[10px] text-slate-500 shrink-0 font-bold">วันที่:</span>
                  <select 
                    value={filterDay}
                    onChange={(e) => setFilterDay(e.target.value)}
                    className="bg-transparent border-none text-xs text-slate-700 focus:outline-none flex-1 font-semibold"
                  >
                    <option value="all">ทั้งหมด (60 วัน)</option>
                    {Array.from({ length: 60 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>วันที่ {d}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-1.5 bg-slate-50 rounded-xl px-2 py-1.5 border border-slate-100">
                  <span className="text-[10px] text-slate-500 shrink-0 font-bold">สถานะ:</span>
                  <select 
                    value={filterPublished}
                    onChange={(e) => setFilterPublished(e.target.value)}
                    className="bg-transparent border-none text-xs text-slate-700 focus:outline-none flex-1 font-semibold"
                  >
                    <option value="all">สถานะทั้งหมด</option>
                    <option value="published">เผยแพร่แล้ว</option>
                    <option value="draft">ยังไม่เผยแพร่</option>
                  </select>
                </div>
              </div>
            </div>

            {/* List with 120 Article slots */}
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
              {filteredArticles.length === 0 ? (
                <div className="p-8 text-center bg-slate-50 rounded-2xl text-slate-400 text-xs sm:text-sm">
                  ไม่พบไฟล์บทความที่ค้นหา ลองปรับแต่งฟิลเตอร์
                </div>
              ) : (
                filteredArticles.map((art) => {
                  const isSelected = selectedArticle?.id === art.id;
                  return (
                    <button
                      key={art.id}
                      onClick={() => {
                        setSelectedArticle(art);
                        setIsEditMode(false);
                      }}
                      className={`w-full text-left p-3 rounded-2xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                        isSelected 
                          ? 'bg-pink-50/80 border-[#E60067] shadow-xs' 
                          : 'bg-white border-slate-100 hover:bg-slate-50'
                      }`}
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold text-[#E60067] bg-pink-100 px-2 py-0.5 rounded-full shrink-0">
                            วันที่ {art.dayNumber}
                          </span>
                          <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full shrink-0">
                            บทความที่ {art.articleNumber}
                          </span>
                          {art.published ? (
                            <span className="text-[9px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">
                              เผยแพร่แล้ว
                            </span>
                          ) : (
                            <span className="text-[9px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">
                              ฉบับร่าง
                            </span>
                          )}
                        </div>
                        <h3 className="text-xs sm:text-sm font-bold text-slate-800 truncate">
                          {art.title}
                        </h3>
                        <p className="text-[10px] text-slate-400 truncate">
                          คีย์เวิร์ด: {art.targetKeyword}
                        </p>
                      </div>
                      <div className="shrink-0">
                        {art.id === 'day-1-art-1' ? (
                          <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">
                            ✓
                          </div>
                        ) : (
                          <div className={`w-2.5 h-2.5 rounded-full ${art.published ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Quick Informational Notice */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50/50 p-4 rounded-3xl border border-amber-100 text-xs space-y-2 shadow-xs">
            <h4 className="font-bold text-amber-900 flex items-center gap-1.5">
              <Info className="w-4 h-4 text-amber-600 shrink-0" />
              <span>ข้อมูลระบบ MemberRoom (60-Day Campaign)</span>
            </h4>
            <p className="text-amber-800 leading-relaxed font-medium">
              สัญญาระบบจะเริ่มแคมเปญเขียนบทความคุณภาพวันละ 2 บทความ (เช้า-เย็น) เป็นเวลา 60 วันเพื่อทำ SEO 
              ผู้แนะนำหลักถูกตั้งค่าเป็นทีมงาน <strong>PinkGiff (รหัสผู้แนะนำ: 132000061)</strong> 
              เมื่อแต่งบทความเสร็จคุณสามารถเลือกเปิดสวิตช์ "เผยแพร่ทันที" เพื่ออัปเดตลงหน้าบ้าน
            </p>
          </div>
        </section>

        {/* Right Side: Article Editor and Live Responsive Preview Screen */}
        <section className="lg:col-span-7 space-y-4">
          {selectedArticle ? (
            <div className="bg-white rounded-3xl shadow-xs border border-pink-100 overflow-hidden flex flex-col min-h-[600px]">
              {/* Permanent Live Article URL Slug Bar */}
              <div className="bg-gradient-to-r from-pink-50 via-rose-50/50 to-white px-4 py-3 sm:px-6 border-b border-pink-100 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="p-1.5 rounded-lg bg-pink-100 text-[#E60067] shrink-0">
                    <Globe className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#E60067]">
                        ลิงก์จริงอัตโนมัติ (Live Public URL)
                      </span>
                      {selectedArticle.published ? (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black">
                          ● เผยแพร่แล้ว (Published)
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
                          รอเผยแพร่ (Draft)
                        </span>
                      )}
                    </div>
                    <div className="text-xs sm:text-sm font-bold text-slate-800 font-mono truncate max-w-[280px] sm:max-w-md">
                      {window.location.origin}/{decodeURIComponent(selectedArticle.slug)}/
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleCopySlugUrl(selectedArticle.slug)}
                    className="px-3 py-1.5 rounded-xl bg-white hover:bg-pink-50 text-slate-700 hover:text-[#E60067] border border-pink-200 text-xs font-bold transition-all shadow-2xs flex items-center gap-1.5 cursor-pointer"
                    title="คัดลอกลิงก์ไปยังคลิปบอร์ด"
                  >
                    {copiedUrl ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-emerald-700">คัดลอกแล้ว!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 text-slate-500" />
                        <span>คัดลอกลิงก์</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOpenLiveArticle(selectedArticle.slug)}
                    className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-[#E60067] to-[#D80056] hover:from-[#C20048] hover:to-[#B0003E] text-white text-xs font-bold shadow-xs hover:shadow transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
                    title="เปิดดูหน้าบทความจริงบนเว็บไซต์ทันที"
                  >
                    <span>เปิดดูหน้าบทความจริง</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Tab Header Selector */}
              <div className="bg-slate-50 border-b border-slate-100 px-4 py-3 sm:px-6 flex items-center justify-between gap-4">
                <div className="flex items-center gap-1 bg-white p-1 rounded-xl border">
                  <button
                    onClick={() => setIsEditMode(false)}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer transition-all ${
                      !isEditMode 
                        ? 'bg-[#E60067] text-white' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Eye className="w-3.5 h-3.5" /> ตัวอย่างพรีวิวบทความจริง (SEO)
                  </button>
                  <button
                    onClick={() => setIsEditMode(true)}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer transition-all ${
                      isEditMode 
                        ? 'bg-[#E60067] text-white' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Edit3 className="w-3.5 h-3.5" /> แก้ไขเนื้อหาบทความ
                  </button>
                </div>

                {/* Quick Save Action Block */}
                <button
                  onClick={() => handleSaveArticle(selectedArticle)}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-1 shadow-md shadow-emerald-500/10 cursor-pointer active:scale-95 transition-all"
                >
                  <Save className="w-4 h-4" /> บันทึกและเผยแพร่
                </button>
              </div>

              {/* View/Editor Working Canvas */}
              <div className="p-4 sm:p-6 md:p-8 flex-1 overflow-y-auto max-h-[700px]">
                {isEditMode ? (
                  /* Editor Mode Form Input fields */
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">หัวข้อหลักบทความ (Title)</label>
                        <input 
                          type="text"
                          value={selectedArticle.title}
                          onChange={(e) => setSelectedArticle({ ...selectedArticle, title: e.target.value })}
                          className="w-full px-3 py-2 border rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#E60067]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">URL Slug (3-5 คำ ภาษาอังกฤษหรือไทย)</label>
                        <input 
                          type="text"
                          value={selectedArticle.slug}
                          onChange={(e) => setSelectedArticle({ ...selectedArticle, slug: e.target.value })}
                          className="w-full px-3 py-2 border rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#E60067]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">คีย์เวิร์ดเป้าหมาย SEO (Target Keyword)</label>
                        <input 
                          type="text"
                          value={selectedArticle.targetKeyword}
                          onChange={(e) => setSelectedArticle({ ...selectedArticle, targetKeyword: e.target.value })}
                          className="w-full px-3 py-2 border rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#E60067]"
                        />
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
                          <input 
                            type="checkbox"
                            checked={selectedArticle.published}
                            onChange={(e) => setSelectedArticle({ ...selectedArticle, published: e.target.checked })}
                            className="w-4 h-4 text-[#E60067] focus:ring-[#E60067] border-slate-300 rounded"
                          />
                          <span>เผยแพร่เผยแพร่ทันที (Publish Immediately)</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Meta Description (120-160 ตัวอักษรดึงดูดคลิก)</label>
                      <textarea 
                        rows={2}
                        value={selectedArticle.description}
                        onChange={(e) => setSelectedArticle({ ...selectedArticle, description: e.target.value })}
                        className="w-full px-3 py-2 border rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#E60067] resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">เนื้อหาบทความแบบ Markdown (เขียนยาว 1,500 - 2,000+ คำตามไกด์ไลน์)</label>
                      <textarea 
                        rows={16}
                        value={selectedArticle.content}
                        onChange={(e) => setSelectedArticle({ ...selectedArticle, content: e.target.value })}
                        className="w-full px-3 py-3 border rounded-2xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#E60067] font-mono leading-relaxed"
                      />
                    </div>
                  </div>
                ) : (
                  /* Live SEO Rendering Mode conforming completely to guidelines */
                  <article className="prose max-w-none text-slate-700">
                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/60 mb-6 flex flex-col gap-2 text-xs">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-slate-400 font-bold block">SEO Meta Title:</span>
                          <span className="text-slate-700 font-semibold">{selectedArticle.title}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-bold block">SEO Target Keyword:</span>
                          <span className="text-pink-600 font-bold">{selectedArticle.targetKeyword}</span>
                        </div>
                      </div>
                      <div className="border-t pt-2">
                        <span className="text-slate-400 font-bold block">Meta Description:</span>
                        <span className="text-slate-600 italic">{selectedArticle.description}</span>
                      </div>
                    </div>

                    {/* Rendered markdown payload */}
                    {renderMarkdown(selectedArticle.content)}
                  </article>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-12 text-center border shadow-xs text-slate-400 flex flex-col items-center justify-center min-h-[400px]">
              <BookOpen className="w-12 h-12 text-slate-300 mb-2" />
              <span>กรุณาเลือกหรือกรอกข้อมูลบทความที่ต้องการพรีวิว</span>
            </div>
          )}
        </section>

      </div>

      {/* Modal แจ้งเตือนเมื่อกดบันทึกและเผยแพร่ พร้อมปุ่มกดเปิดดูหน้าเว็บจริงทันที */}
      {publishedModalArticle && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-pink-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-400 text-white flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
              <Sparkles className="w-7 h-7" />
            </div>

            <div className="text-center space-y-2 mb-6">
              <h3 className="text-lg sm:text-xl font-black text-slate-900">
                บันทึกและเผยแพร่ลงเว็บไซต์จริงสำเร็จ!
              </h3>
              <p className="text-xs sm:text-sm text-slate-600">
                บทความของคุณออนไลน์แล้ว พร้อมดึงดูดผู้ค้นหาใน Google และปิดการสมัครตัวแทน PinkGiff
              </p>
            </div>

            <div className="bg-pink-50/60 p-4 rounded-2xl border border-pink-100 space-y-2 mb-6">
              <div className="text-[11px] font-bold text-[#E60067] uppercase flex items-center justify-between">
                <span>ลิงก์ URL บทความจริง</span>
                <span className="text-emerald-700 font-extrabold">● LIVE ON WEBSITE</span>
              </div>
              <div className="text-xs sm:text-sm font-mono font-bold text-slate-800 break-all bg-white p-3 rounded-xl border border-pink-200">
                {window.location.origin}/{decodeURIComponent(publishedModalArticle.slug)}/
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => {
                  const slug = publishedModalArticle.slug;
                  setPublishedModalArticle(null);
                  handleOpenLiveArticle(slug);
                }}
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-[#E60067] to-[#D80056] hover:from-[#C20048] hover:to-[#B0003E] text-white text-xs sm:text-sm font-black shadow-md shadow-pink-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
              >
                <span>👉 เปิดดูหน้าบทความจริงทันที</span>
                <ExternalLink className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => {
                  handleCopySlugUrl(publishedModalArticle.slug);
                }}
                className="py-3 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {copiedUrl ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-700">คัดลอกแล้ว</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 text-slate-600" />
                    <span>คัดลอกลิงก์</span>
                  </>
                )}
              </button>
            </div>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setPublishedModalArticle(null)}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                ปิดหน้าต่างนี้และแก้ไขต่อ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
